import { useCallback, useEffect, useMemo, useState } from 'react';
import useFeatureEntitlements, { CONDITIONS_FEATURE_ENTITLEMENTS } from '@alltrails/context/hooks/useFeatureEntitlements';
import { updateDynamicStyleIds } from '@alltrails/maps/redux/reducer';
import type { ConditionStyleId } from '@alltrails/maps/types/Styles';
import { conditionsStyleIds } from '@alltrails/maps/utils/constants';
import { useAppDispatch, useAppSelector } from '../redux/helpers';

const useConditionsValidation = () => {
  const dispatch = useAppDispatch();
  const { dynamicStyleIds } = useAppSelector(state => ({
    dynamicStyleIds: state.map.dynamicStyleIds
  }));
  const { hasEveryFeatureEntitlement } = useFeatureEntitlements();

  const [hasShownConditionsUpsellModal, setHasShownConditionsUpsellModal] = useState(false);
  const [showConditionsUpsellModal, setShowConditionsUpsellModal] = useState(false);
  const onConditionsUpsellModalCloseRequest = useCallback(() => setShowConditionsUpsellModal(false), []);
  const [conditionsUpsellModalApplyStylesCallback, setConditionsUpsellModalApplyStylesCallback] = useState<() => void>();

  const handleConditionsFailedEntitlementCheck = useCallback(async (applyStyles: () => void) => {
    setShowConditionsUpsellModal(true);
    setHasShownConditionsUpsellModal(true);
    setConditionsUpsellModalApplyStylesCallback(() => applyStyles);
  }, []);

  useEffect(() => {
    if (hasShownConditionsUpsellModal && !showConditionsUpsellModal) {
      // After the upsell modal is closed, if the user signed up for peak, apply the conditions styles, otherwise, remove them
      if (hasEveryFeatureEntitlement({ entitlements: CONDITIONS_FEATURE_ENTITLEMENTS })) {
        conditionsUpsellModalApplyStylesCallback?.();
      } else {
        dispatch(updateDynamicStyleIds(dynamicStyleIds.filter(id => !conditionsStyleIds.includes(id as ConditionStyleId))));
      }
      setConditionsUpsellModalApplyStylesCallback(undefined);
    }
    // We only want to check this when the modal closes.
    // The upsell modals currently do not support an onFailure/onSuccess callback, which would be preferred,
    // and we don't want to make a risky change to those upsells right before peak launch.
  }, [showConditionsUpsellModal]);

  return useMemo(
    () => ({
      showConditionsUpsellModal,
      onConditionsUpsellModalCloseRequest,
      handleConditionsFailedEntitlementCheck
    }),
    [handleConditionsFailedEntitlementCheck, onConditionsUpsellModalCloseRequest, showConditionsUpsellModal]
  );
};

export default useConditionsValidation;
